import { FC, useEffect, useMemo } from 'react';
import { Product } from '../../../common/model';
import ProductComponent from '../../product/ProductComponent';
import './ProductListComponent.scss';
import { useCallback } from 'react';
import { AppActions } from '../../../redux/AppActions';
import { FavoriteSkeletonComponent } from './FavoriteSkeletonComponent/FavoriteSkeletonComponent';
import { useAppDispatch } from '../../../redux/store';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/AppReducer';
interface IProductListComponentProps {
	products: Product[];
	viewMode: string;
	isProfilePage?: boolean;
	isLoading?: boolean;
}

export const FavoriteProductListComponent: FC<IProductListComponentProps> = ({
	products,
	isProfilePage,
	isLoading
}) => {
	const dispatch = useAppDispatch();
	const showPageTitleCallback = useCallback(
		() => dispatch(AppActions.setAppTitle(undefined)),
		[dispatch]
	);
	useEffect(() => {
		showPageTitleCallback();
	}, []);
	const cleanIsOnFavorites = useCallback(
		() => dispatch(AppActions.isOnFavoritesPage(false)),
		[dispatch]
	);
	useEffect(() => {
		return () => {
			cleanIsOnFavorites();
		};
	}, [cleanIsOnFavorites]);

	const isGroupByHallEnabled = useSelector((state: RootState) => state.appState.groupByHallNumbers);

	const groupedProducts = useMemo(() => {
		if (products.length === 0) return {};
		const grouped: Record<string, Product[]> = products.reduce((acc, product) => {
			const hallName = product.brand?.hall_name;
			const boothNumber = product.brand?.boothNumber;

			//Grouping products
			if (hallName) {
				if (!acc[hallName]) acc[hallName] = [];
				acc[hallName].push(product);
			} else if (boothNumber) {
				if (!acc['Hall Info Unavailable']) acc['Hall Info Unavailable'] = [];
				acc['Hall Info Unavailable'].push(product);
			} else {
				if (!acc['Not Exhibiting at Expo West']) acc['Not Exhibiting at Expo West'] = [];
				acc['Not Exhibiting at Expo West'].push(product);
			}

			return acc;
		}, {} as Record<string, Product[]>);

		//Sorting hall numbers
		const sortedKeys = Object.keys(grouped).sort((a, b) => {
			if (a === 'Not Exhibiting at Expo West') return 1; // last
			if (b === 'Not Exhibiting at Expo West') return -1;

			if (a === 'Hall Info Unavailable') return 1; // second last
			if (b === 'Hall Info Unavailable') return -1;

			return a.localeCompare(b);
		});

		//Build sorted object
		return sortedKeys.reduce((sortedAcc: Record<string, Product[]>, key) => {
			sortedAcc[key] = grouped[key];
			return sortedAcc;
		}, {});
	}, [products]);

	const noExpoProducts = useMemo(() => {
		const keys = Object.keys(groupedProducts);
		return keys.length === 1 && keys[0] === 'Not Exhibiting at Expo West';
	}, [groupedProducts]);

	useEffect(() => {
		dispatch(AppActions.setDisableGroupByHall(noExpoProducts));
	}, [noExpoProducts, dispatch]);

	return (
		<div
			data-testid='favorite-list'
			className={`favorite-list ${isGroupByHallEnabled ? 'grouped' : ''}`}
		>
			{!isLoading ? (
				<div className='products-wrapper favorite-products-wrapper'>
					{isGroupByHallEnabled ? (
						// Runs when Group by hall is selected
						Object.keys(groupedProducts).map(hall => (
							<div className='hall-group' key={hall}>
								<h3 className='hall-title'>{hall}</h3>
								<div className={`product-list ${!isProfilePage ? 'favorites-wrapper' : ''}`}>
									{groupedProducts[hall].map(product => (
										<ProductComponent favorite={true} key={product.id} product={product} />
									))}
								</div>
							</div>
						))
					) : (
						<div className={`product-list ${!isProfilePage ? 'favorites-wrapper' : ''}`}>
							<div>
								{products.map(product => (
									<ProductComponent favorite={true} key={product.id} product={product} />
								))}
							</div>
						</div>
					)}
				</div>
			) : (
				<FavoriteSkeletonComponent />
			)}
		</div>
	);
};
